import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe, NgClass, NgStyle } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { TagBoxComponent } from '../message-box/tag-box.component';
import {
  DigitalIdResponse,
  FrankieOneWorkflowIssue,
  KycStatus,
  KycStatusMessage,
  KycVerificationError,
  KycHistoryRecord,
  KycEventType
} from '@portal-workspace/grow-shared-library';
import { environment } from 'apps/portal2/src/environments/environment';

@Component({
  selector: 'kyc-verification-details',
  templateUrl: './kyc-verification-details.component.html',
  styleUrls: ['./kyc-verification-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FlexModule,
    MatButtonModule,
    MatTooltipModule,
    MessageBoxComponent,
    TagBoxComponent,
    DatePipe,
    NgClass,
    NgStyle
  ]
})
export class KycVerificationDetailsComponent {
  @Input() result?: any; // DigitalIdResult | DigitalIdResultReceived | any
  @Input({ required: true }) isExpanded!: boolean;
  @Input() showUpdateKycButton = false;
  @Input() showSyncFromFrankieOneButton = false;
  @Input() syncInProgress = false;
  @Input() showFrankieOneButton = false;
  @Input() showIssues = false;
  @Input() kycStatus?: KycStatus; // Optional fallback when result.status is not available
  @Input() history?: KycHistoryRecord[]; // KYC verification history

  // Send KYC verification SMS button, shown alongside Update KYC. Disabled state/tooltip and the
  // in-progress spinner are computed by the parent (depends on consent status and loader state).
  @Input() showSendSmsButton = false;
  @Input() sendSmsDisabled = false;
  @Input() sendSmsTooltip = '';
  @Input() sendSmsInProgress = false;

  @Output() toggleExpand = new EventEmitter<void>();
  @Output() syncFromFrankieOne = new EventEmitter<void>();
  @Output() updateKyc = new EventEmitter<void>();
  @Output() openFrankieOne = new EventEmitter<void>();
  @Output() sendSms = new EventEmitter<void>();

  // Computed properties to extract data from result
  get status(): any {
    // Handle both DigitalIdResult structure (with status at top level)
    // and direct DigitalIdResponse structure (status needs to come from parent via kycStatus input)
    return this.result?.status || this.kycStatus;
  }

  get lastUpdate(): Date | undefined {
    return this.result?.lastUpdate;
  }

  get digitalIdResponse(): DigitalIdResponse | any {
    // Handle both DigitalIdResult (with nested digitalIdResponse) and DigitalIdResponse directly
    if (!this.result) return null;
    
    // Check if it has digitalIdResponse property (DigitalIdResult structure)
    if (this.result.digitalIdResponse) {
      return this.result.digitalIdResponse;
    }
    
    // Check if it has frankieOneCallback (indicating it's a DigitalIdResponse directly)
    if (this.result.frankieOneCallback || this.result.verification_status) {
      return this.result;
    }
    
    return null;
  }

  get frankieOneEntityId(): string | null {
    // Primary source: dedicated column from database
    if (this.result?.frankieOneEntityId) {
      return this.result.frankieOneEntityId;
    }
    
    // Fallback 1: from callback in digitalIdResponse
    if (this.digitalIdResponse?.frankieOneCallback?.entityId) {
      return this.digitalIdResponse.frankieOneCallback.entityId;
    }
    
    // Fallback 2: from transaction_id in digitalIdResponse
    if (this.digitalIdResponse?.transaction_id) {
      return this.digitalIdResponse.transaction_id;
    }
    
    return null;
  }

  get verificationError(): KycVerificationError | null {
    const response = this.digitalIdResponse;
    return response?.verificationError || response?.detailsMismatchError || null;
  }

  get issues(): FrankieOneWorkflowIssue[] {
    const response = this.digitalIdResponse;
    const issuesList = response?.frankieOneCallback?.issues || [];
    
    // Sort by severity (most severe first)
    const severityOrder = ['CRITICAL', 'ERROR', 'BLOCK', 'WARNING', 'REVIEW', 'INFO'];
    return [...issuesList].sort((a, b) =>
      severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity)
    );
  }

  // Computed property for status message
  get statusMessage(): KycStatusMessage {
    if (!this.status) {
      return {
        type: 'info',
        message: 'No verification results.',
        showDetails: false,
        showPrint: false
      };
    }

    switch (this.status) {
      case 'RECEIVED_ERROR':
      case 'RECEIVED_FAILED':
        return {
          type: 'danger',
          message: this.verificationError?.userMessage || `Verification failed, please contact support for details.`,
          showDetails: true,
          showPrint: true,
          detailsTooltip: 'Expand digital Id verification result',
          printIcon: 'mdi-printer-outline'
        };
      case 'RECEIVED_COMPLETE':
        return {
          type: 'info',
          message: 'Digital ID has been verified successfully',
          showDetails: true,
          showPrint: true,
          detailsTooltip: 'Expand digital Id verification result',
          printIcon: 'mdi-printer'
        };
      case 'RECEIVED_PENDING_REVIEW':
        return {
          type: 'warn',
          message: 'Digital ID verification is under review by our team.',
          showDetails: true,
          showPrint: true,
          detailsTooltip: 'Expand digital Id verification result',
          printIcon: 'mdi-printer-outline'
        };
      case 'RECEIVED_INCOMPLETE':
        return {
          type: 'warn',
          message: this.verificationError?.userMessage || 'Digital ID verification incomplete',
          showDetails: true,
          showPrint: true,
          detailsTooltip: 'Expand digital Id verification result',
          printIcon: 'mdi-printer-outline'
        };
      case 'WAITING':
        return {
          type: 'info',
          message: 'Please complete the verification process to continue.',
          showDetails: true,
          showPrint: false,
          detailsTooltip: 'Expand verification details'
        };
      case 'SMS_SENT':
        return {
          type: 'info',
          message: 'Verification SMS sent - awaiting completion',
          showDetails: true,
          showPrint: false,
          detailsTooltip: 'Expand verification details'
        };
      default:
        return {
          type: 'info',
          message: 'No verification results.',
          showDetails: false,
          showPrint: false
        };
    }
  }

  verificationStatusDisplay(status: any): string {
    switch (status) {
      case 'RECEIVED_COMPLETE':
        return 'Verified';
      case 'RECEIVED_FAILED':
        return 'Failed';
      case 'RECEIVED_PENDING_REVIEW':
        return 'Under Review';
      case 'RECEIVED_INCOMPLETE':
        return 'Incomplete';
      case 'RECEIVED_ERROR':
        return 'System Error';
      case 'SMS_SENT':
        return 'SMS Sent';
      case 'WAITING':
        return 'Pending';
      default:
        return status ? status.toString().replace(/_/g, ' ') : 'Not available';
    }
  }

  requestStatusDisplay(verificationStatus?: string): string {
    if (verificationStatus) {
      switch (verificationStatus) {
        case 'completed':
          return 'Completed';
        case 'in_progress':
          return 'In Progress';
        case 'failed':
          return 'Failed';
        case 'pending_review':
          return 'Under Review';
        default:
          return 'Not available';
      }
    }
    return 'Not available';
  }

  getSeverityClass(severity: string): string {
    switch (severity) {
      case 'CRITICAL':
      case 'ERROR': return 'severity-critical';
      case 'BLOCK': return 'severity-block';
      case 'WARNING': return 'severity-warning';
      case 'REVIEW': return 'severity-review';
      default: return 'severity-info';
    }
  }

  formatIssueType(issue: string): string {
    return issue.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  defaultString(s: any): string {
    return s ? s : 'Not available';
  }

  capitalize(s: any): string {
    if (!s) return s;
    const str = s.toString();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  onToggleExpand(event: Event): void {
    event.stopPropagation();
    this.toggleExpand.emit();
  }

  onUpdateKyc(event: Event): void {
    event.stopPropagation();
    this.updateKyc.emit();
  }

  get syncDisabled(): boolean {
    return !this.frankieOneEntityId;
  }

  get syncTooltip(): string {
    return this.syncDisabled
      ? 'No FrankieOne entity linked yet'
      : 'Sync the latest KYC status and risk from FrankieOne';
  }

  onSyncFromFrankieOne(event: Event): void {
    event.stopPropagation();
    if (this.syncDisabled || this.syncInProgress) {
      return;
    }
    this.syncFromFrankieOne.emit();
  }

  onSendSms(event: Event): void {
    event.stopPropagation();
    this.sendSms.emit();
  }

  onOpenFrankieOne(event: Event): void {
    event.stopPropagation();
    const entityId = this.frankieOneEntityId;
    if (entityId) {
      const portalUrl = environment.frankieOnePortalUrl;
      const url = `${portalUrl}/entity/${entityId}`;
      window.open(url, '_blank');
    }
    this.openFrankieOne.emit();
  }

  // Status helper methods
  isStatusError(): boolean {
    return this.status === 'RECEIVED_ERROR';
  }

  isStatusIncomplete(): boolean {
    return this.status === 'RECEIVED_INCOMPLETE';
  }

  isStatusFailed(): boolean {
    return this.status === 'RECEIVED_FAILED';
  }

  // History helper methods
  get hasHistory(): boolean {
    return !!this.history && this.history.length > 0;
  }

  get sortedHistory(): KycHistoryRecord[] {
    if (!this.history) return [];
    return [...this.history].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  getEventTypeDisplay(eventType: KycEventType): string {
    switch (eventType) {
      case 'SMS_SENT': return 'SMS Sent';
      case 'CALLBACK_RECEIVED': return 'Verification Result';
      case 'STATUS_CHANGE': return 'Status Updated';
      case 'MANUAL_UPDATE': return 'Manual Update';
      default: return eventType;
    }
  }

  getEventIcon(eventType: KycEventType): string {
    switch (eventType) {
      case 'SMS_SENT': return 'mdi-cellphone-message';
      case 'CALLBACK_RECEIVED': return 'mdi-note-text';
      case 'STATUS_CHANGE': return 'mdi-update';
      case 'MANUAL_UPDATE': return 'mdi-account-edit';
      default: return 'mdi-information';
    }
  }
}
